import { decompressSessionStorage } from '../../../utils/sessionStorage';

interface Args {
  /** El borrador del asistente, tal cual está en sessionStorage. */
  dataFormRaw: string | null;
  /** Pestaña elegida: 0 = OT nueva, 1 = OT existente, 2 = campaña. */
  tabSelected: number;
  /** Lo que el usuario eligió en el modal de OT/campaña, si eligió algo. */
  storedDataOTSelected?: { id?: string; roid?: string | string[] } | null;
}

/**
 * A dónde volver cuando una comunicación se ENCOLA (spec 2026-09-11).
 *
 * 🔴 Antes era `/communication` a secas, viniera el usuario de donde viniera:
 * quien creaba una comunicación DENTRO de una OT genérica o de una campaña
 * acababa en el módulo de comunicaciones, como si hubiera empezado de cero.
 * Reportado en carretera.
 *
 * La regla es la misma que usa el camino con red: se vuelve al PADRE. Solo se
 * cae al módulo cuando de verdad no hay padre al que volver — la pestaña 0
 * («OT nueva»), cuyo padre no existe todavía y lo creará el replay.
 */
export function communicationQueuedRedirect({
  dataFormRaw,
  tabSelected,
  storedDataOTSelected,
}: Args): string {
  let dataForm: {
    parentOrderId?: string;
    parentOrderRoid?: string | string[];
    specialTag?: string;
  } = {};
  try {
    if (dataFormRaw) dataForm = JSON.parse(decompressSessionStorage(dataFormRaw));
  } catch {
    // borrador ilegible: se decide con lo que haya
  }

  // En la pestaña 0 el usuario ha dicho «crea una OT nueva»: el padre NO existe
  // todavía, y una selección que hubiera quedado del modal no es su padre. Se
  // sigue la misma regla que el guardado, que para ese caso manda al módulo.
  //
  // Ojo con el caso mixto, que es real: la pestaña puede decir 0 mientras el
  // operario venía DE una OT (el preselect no llegó a Redux). Eso se reconoce
  // porque el borrador trae `parentOrderId` heredado y el modal está vacío —
  // ahí sí hay padre y sí se vuelve a él.
  const seleccionDelModal = tabSelected === 0 ? undefined : storedDataOTSelected?.id;
  const parentId = seleccionDelModal || dataForm.parentOrderId;
  if (!parentId) return '/communication';

  const roidRaw =
    (tabSelected === 0 ? undefined : storedDataOTSelected?.roid) ??
    dataForm.parentOrderRoid;
  const roid = Array.isArray(roidRaw) ? roidRaw.join('.') : roidRaw;
  // Una campaña se reconoce por la pestaña o por su ROID («Ca…»), igual que en
  // el camino con red.
  const isCampaign = tabSelected === 2 || !!roid?.startsWith('Ca');

  return isCampaign
    ? `/work-orders/campaign/${parentId}`
    : `/work-orders/${parentId}`;
}
